// PostHog event names + prop builders for the Lead Agent paywall (RET-1230 /
// C2). Mirrors the per-domain constants pattern (see wapiAnalytics.constants).
// The three event names come from the D8 v1.11 instrumentation plan:
//   la_paywall_banner_viewed: emitted by the acceptance banner (Unit 4)
//   la_paywall_modal_opened: emitted by the confirm modal (Unit 5)
//   la_paywall_paused_clicked: emitted by the decline/pause flow (Unit 6)

import {
  ePlanCode,
  ePlanFrequency,
} from 'src/providers/SubscriptionProvider/Subscription.enums';
import type {
  tLeadAgentBannerVariant,
  tLeadAgentSelectableTier,
  tLeadAgentWorkspaceBucket,
} from 'src/providers/LeadAgentAcceptanceProvider/LeadAgentAcceptance.types';

export const LEAD_AGENT_PAYWALL_EVENTS = {
  BANNER_VIEWED: 'la_paywall_banner_viewed',
  MODAL_OPENED: 'la_paywall_modal_opened',
  PAUSED_CLICKED: 'la_paywall_paused_clicked',
  // Seller-facing paused indicator (home + embudos). Distinct from the owner-facing
  // BANNER_VIEWED: different audience (user_role=seller), feeding the "sellers aware of
  // a paused bot" metric node, not the owner conversion funnel. Name locked; recorded
  // in the cycle Decisions Log alongside the D8 plan.
  SELLER_PAUSED_VIEWED: 'la_seller_bot_paused_viewed',
  // C5 self-serve tier-upgrade funnel (RET-1614). The upgrade conversion is also
  // observable in billing/Stripe, but these give the PostHog funnel its
  // confirm→succeeded/failed steps (drop-off, conversion rate) that MODAL_OPENED
  // alone can't. All carry `entry_point` + `target_tier`. Add to
  // intelligence/signals/tracking-plan.md on merge (status pending-deploy).
  UPGRADE_CONFIRMED: 'la_paywall_upgrade_confirmed',
  UPGRADE_SUCCEEDED: 'la_paywall_upgrade_succeeded',
  UPGRADE_FAILED: 'la_paywall_upgrade_failed',
  // AGI-766 (MLA-100): the Owner closed the chat's commercial pause notice for
  // the whole workspace, with or without saying why. Emitted only after billing
  // confirmed the close. Read next to BANNER_VIEWED (surface=chat). Add to
  // intelligence/signals/tracking-plan.md on merge (status pending-deploy).
  CHAT_BANNER_DISMISSED: 'la_chat_banner_dismissed',
} as const;

/** D8 plan colour buckets: info + fomo report yellow, urgent reports red. */
export type tLeadAgentPaywallBannerColor = 'yellow' | 'red';

/** D8 plan tier mapped from the subscription plan code. */
export type tLeadAgentPaywallPlan = 'pro' | 'advanced';

/** D8 plan periodicity mapped from the subscription frequency. */
export type tLeadAgentPaywallPeriodicity =
  | 'monthly'
  | 'quarterly'
  | 'semiannual'
  | 'annual';

/** Surface the banner is rendered on: a route, or the open chat's composer. */
export type tLeadAgentPaywallSurface =
  | 'home'
  | 'funnels'
  | 'automations'
  | 'chat';

/**
 * Props of `la_paywall_banner_viewed` (Owner/Admin) and
 * `la_seller_bot_paused_viewed` (everyone else) when the chat composer shows
 * the billing-paused banner. `pause_reason` is the workspace's pause reason,
 * e.g. `cap_reached`, or `unknown` when none was given.
 */
export type tLeadAgentChatPausedViewedProps = {
  surface: Extract<tLeadAgentPaywallSurface, 'chat'>;
  pause_reason: string;
};

/**
 * Props of `la_chat_banner_dismissed` (AGI-766). `reason` is the Owner's answer,
 * null when skipped (then `skipped` is true). `pause_reason` and
 * `days_since_trial_end` come from billing's answer; `ever_used` is MLA-51's
 * everUsed as the Owner's session knew it, null when unknown.
 */
export type tLeadAgentChatBannerDismissedProps = {
  surface: Extract<tLeadAgentPaywallSurface, 'chat'>;
  pause_reason: string;
  reason: string | null;
  skipped: boolean;
  has_reason_text: boolean;
  ever_used: boolean | null;
  days_since_trial_end: number | null;
};

export type tLeadAgentBannerViewedProps = {
  banner_color: tLeadAgentPaywallBannerColor;
  days_until_paywall: number | null;
  plan: tLeadAgentPaywallPlan | null;
  plan_periodicity: tLeadAgentPaywallPeriodicity | null;
  suggested_tier: Lowercase<tLeadAgentSelectableTier>;
  surface: tLeadAgentPaywallSurface;
  /** Only present when the cohort bucket is known. */
  workspace_bucket?: tLeadAgentWorkspaceBucket;
};

/** info + fomo are yellow, urgent is red (D8 plan, Open Questions resolved). */
export function mapBannerColor(
  variant: tLeadAgentBannerVariant,
): tLeadAgentPaywallBannerColor {
  return variant === 'urgent' ? 'red' : 'yellow';
}

/** PLAN_PROFESSIONAL -> pro; PLAN_ENTERPRISE (PLAN_ADVANCED) -> advanced. */
export function mapPaywallPlan(
  planCode: ePlanCode | null | undefined,
): tLeadAgentPaywallPlan | null {
  if (planCode === ePlanCode.PLAN_PROFESSIONAL) return 'pro';
  if (planCode === ePlanCode.PLAN_ENTERPRISE) return 'advanced';
  return null;
}

const PERIODICITY_BY_FREQUENCY: Partial<
  Record<ePlanFrequency, tLeadAgentPaywallPeriodicity>
> = {
  [ePlanFrequency.MONTHLY]: 'monthly',
  [ePlanFrequency.QUARTERLY]: 'quarterly',
  [ePlanFrequency.BIANNUAL]: 'semiannual',
  [ePlanFrequency.ANNUALLY]: 'annual',
};

export function mapPaywallPeriodicity(
  frequency: ePlanFrequency | null | undefined,
): tLeadAgentPaywallPeriodicity | null {
  if (!frequency) return null;
  return PERIODICITY_BY_FREQUENCY[frequency] ?? null;
}
